/**
 * Yemeksepeti adisyon (müşteri fişi) ürün satırı üretici.
 *
 * Bu helper, hem `YemeksepetiOrderDetailModal` (manuel "Fişi Yazdır" butonu)
 * hem de `useKitchenPrintHandlers.handleReceiptPrint` (otomatik yazdırma)
 * tarafından kullanılır. Tek bir kaynak olması sayesinde ürün adları,
 * adetler, toppingler, choices, indirimler ve yorumlar **aynı şekilde**
 * fişe basılır.
 *
 * Modal'da satır 592-640 arasında bulunan logic 1:1 buraya taşındı.
 */
import { translateYsDiscountName, translateYsSponsor } from './helpers';
import type { PackageReceiptItem } from '../components/PackageReceiptPrintService';

// ─────────────────────────────────────────────────────────────────────────────
// TİPLER (Modal'dakilerin birebir karşılığı — re-export edilmiyor; modal kendi
// public API'sini koruyor, sadece dahili helper olarak burada da yaşıyorlar.)
// ─────────────────────────────────────────────────────────────────────────────

export type DiscountSponsor = 'PLATFORM' | 'VENDOR' | 'THIRD_PARTY' | string;

export interface SponsorshipBreakdown {
  sponsor: DiscountSponsor;
  amount: number;
}

export interface DiscountWithSponsorship {
  name: string;
  amount: number;
  sponsorships?: SponsorshipBreakdown[];
}

export interface RawDiscount extends DiscountWithSponsorship {
  discountType?: string;
}

export interface RawProductOption {
  id?: string;
  name: string;
  price?: string | number;
  quantity?: number;
  type?: string;
  children?: RawProductOption[];
  discounts?: DiscountWithSponsorship[];
}

export interface RawProduct {
  id?: string;
  name: string;
  unitPrice?: string | number;
  paidPrice?: string | number;
  quantity?: string | number;
  comment?: string;
  variation?: { name?: string };
  selectedToppings?: RawProductOption[];
  selectedChoices?: RawProductOption[];
  discounts?: DiscountWithSponsorship[];
}

/**
 * Yemeksepeti sipariş itemı için minimum bağ. Backend bazen camelCase, bazen
 * PascalCase döndürebileceği için ikisini de defansif okuyoruz.
 */
export interface YsOrderItemLike {
  id?: number;
  name?: string;
  Name?: string;
  quantity?: number;
  Quantity?: number;
  unitPrice?: number;
  UnitPrice?: number;
  paidPrice?: number;
  PaidPrice?: number;
  originalUnitPrice?: number | null;
  OriginalUnitPrice?: number | null;
  comment?: string;
  Comment?: string;
  variationName?: string;
  VariationName?: string;
  toppingsJson?: string;
  ToppingsJson?: string;
  discountsJson?: string | null;
  DiscountsJson?: string | null;
}

export interface YsOrderLike {
  items?: YsOrderItemLike[];
  Items?: YsOrderItemLike[];
  rawJsonData?: string;
  RawJsonData?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER FONKSİYONLAR (Modal'dakilerin birebir kopyası)
// ─────────────────────────────────────────────────────────────────────────────

const normalizeDiscount = (d: any): RawDiscount => {
  const sponsorshipsRaw: any[] = Array.isArray(d?.sponsorships)
    ? d.sponsorships
    : Array.isArray(d?.Sponsorships)
      ? d.Sponsorships
      : [];
  const sponsorships: SponsorshipBreakdown[] = sponsorshipsRaw
    .map((s: any) => ({
      sponsor: (s?.sponsor || s?.Sponsor || '').toString(),
      amount: parseFloat(s?.amount ?? s?.Amount ?? '0') || 0,
    }))
    .filter((s) => s.sponsor && s.amount !== 0);
  return {
    name: d?.name || d?.Name || 'Indirim',
    amount: parseFloat(d?.amount ?? d?.Amount ?? d?.discountAmount ?? '0') || 0,
    discountType: d?.discountType || d?.DiscountType || '',
    sponsorships: sponsorships.length > 0 ? sponsorships : undefined,
  };
};

const normalizeOption = (opt: any): RawProductOption => ({
  ...opt,
  discounts: Array.isArray(opt?.discounts) && opt.discounts.length > 0
    ? opt.discounts.map(normalizeDiscount)
    : undefined,
  children: Array.isArray(opt?.children)
    ? opt.children.map((c: any) => normalizeOption(c))
    : undefined,
});

export const parseYsRawJsonData = (rawJson?: string): {
  deliveryInstructions?: string;
  products: RawProduct[];
  customerComment?: string;
  discounts: RawDiscount[];
  /** YS API `delivery.address.latitude` — kurye için Google Maps QR'ında kullanılır. */
  latitude?: number;
  /** YS API `delivery.address.longitude` — kurye için Google Maps QR'ında kullanılır. */
  longitude?: number;
  /** YS API `expeditionType` — 'pickup' ise müşteri siparişi kendisi alır. */
  expeditionType?: string;
  /** YS API `pickup.pickupTime` (UTC) — Gel Al siparişinde müşterinin geleceği saat. */
  pickupTime?: string;
  /** YS API `pickup.pickupCode` — bazı ülkelerde müşteriye verilen teslim kodu. */
  pickupCode?: string;
} => {
  if (!rawJson) return { products: [], discounts: [] };
  try {
    const data = JSON.parse(rawJson);
    const deliveryAddress = data?.delivery?.address;
    const deliveryInstructions = deliveryAddress?.deliveryInstructions;
    const latRaw = deliveryAddress?.latitude;
    const lngRaw = deliveryAddress?.longitude;
    const latitude = typeof latRaw === 'number'
      ? latRaw
      : (typeof latRaw === 'string' && latRaw.trim() !== '' && !Number.isNaN(Number(latRaw)) ? Number(latRaw) : undefined);
    const longitude = typeof lngRaw === 'number'
      ? lngRaw
      : (typeof lngRaw === 'string' && lngRaw.trim() !== '' && !Number.isNaN(Number(lngRaw)) ? Number(lngRaw) : undefined);
    const products: RawProduct[] = (data?.products || []).map((p: any): RawProduct => ({
      ...p,
      discounts: Array.isArray(p?.discounts) && p.discounts.length > 0
        ? p.discounts.map(normalizeDiscount)
        : undefined,
      selectedToppings: Array.isArray(p?.selectedToppings)
        ? p.selectedToppings.map((t: any) => normalizeOption(t))
        : undefined,
      selectedChoices: Array.isArray(p?.selectedChoices)
        ? p.selectedChoices.map((t: any) => normalizeOption(t))
        : undefined,
    }));
    const customerComment = data?.comments?.customerComment;
    const discounts: RawDiscount[] = (data?.discounts || []).map(normalizeDiscount);
    const expeditionType = data?.expeditionType ? String(data.expeditionType) : undefined;
    const pickupTime = data?.pickup?.pickupTime ? String(data.pickup.pickupTime) : undefined;
    const pickupCodeRaw = data?.pickup?.pickupCode;
    const pickupCode = pickupCodeRaw ? String(pickupCodeRaw) : undefined;
    return {
      deliveryInstructions, products, customerComment, discounts, latitude, longitude,
      expeditionType, pickupTime, pickupCode,
    };
  } catch {
    return { products: [], discounts: [] };
  }
};

/**
 * Verilen lat/lng için Google Maps konum linki üretir.
 *
 * Yemeksepeti adisyon (müşteri fişi) yazdırılırken bu link QR'a çevrilip fişe
 * basılır — kurye telefondan QR'ı okutarak doğrudan haritaya yönlenir. Helper
 * burada toplandı ki manuel basım (YemeksepetiOrderDetailModal) ve otomatik
 * basım (useKitchenPrintHandlers) aynı URL formatını kullansın.
 */
export const buildGoogleMapsUrl = (latitude?: number, longitude?: number): string | undefined => {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return undefined;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
  return `https://www.google.com/maps?q=${latitude},${longitude}`;
};

const getItemDiscounts = (
  item: { discountsJson?: string | null; DiscountsJson?: string | null },
  rawProd?: RawProduct,
): DiscountWithSponsorship[] => {
  const discountsJson = item.discountsJson ?? item.DiscountsJson;
  if (discountsJson) {
    try {
      const parsed = JSON.parse(discountsJson);
      if (Array.isArray(parsed)) return parsed.map(normalizeDiscount);
    } catch { /* fallthrough */ }
  }
  return rawProd?.discounts || [];
};

const formatDiscountSubItems = (
  discounts: DiscountWithSponsorship[] | undefined,
  labelPrefix: string,
  baseIndent: string,
): { name: string }[] => {
  if (!discounts || discounts.length === 0) return [];
  const out: { name: string }[] = [];
  for (const d of discounts) {
    out.push({ name: `${baseIndent}${labelPrefix}: ${translateYsDiscountName(d.name)} -${(d.amount || 0).toFixed(2)} TL` });
    if (d.sponsorships && d.sponsorships.length > 0) {
      out.push({
        name: `${baseIndent}  ${d.sponsorships.map((s) => `${translateYsSponsor(s.sponsor)}: -${(s.amount || 0).toFixed(2)}`).join(' | ')}`,
      });
    }
  }
  return out;
};

const flattenOptions = (opts: any[], depth = 0): { name: string }[] => {
  const result: { name: string }[] = [];
  for (const opt of opts) {
    const name = opt.name || opt.Name || '';
    const price = parseFloat(opt.price ?? opt.Price ?? '0');
    const qty = opt.quantity || opt.Quantity || 1;
    const indent = '  '.repeat(depth);
    result.push({ name: `${indent}+ ${qty > 1 ? qty + 'x ' : ''}${name}${price > 0 ? ` (+${price.toFixed(2)})` : ''}` });
    const optDiscountsRaw: any[] = Array.isArray(opt.discounts)
      ? opt.discounts
      : Array.isArray(opt.Discounts)
        ? opt.Discounts
        : [];
    if (optDiscountsRaw.length > 0) {
      result.push(...formatDiscountSubItems(optDiscountsRaw.map(normalizeDiscount), 'Indirim', '  ' + indent));
    }
    const children: any[] = opt.children || opt.Children || [];
    if (children.length > 0) result.push(...flattenOptions(children, depth + 1));
  }
  return result;
};

// ─────────────────────────────────────────────────────────────────────────────
// ANA HELPER — Modal ve hook tarafından ortak çağrılır
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Yemeksepeti siparişini adisyon fişi ürün satırlarına dönüştürür.
 *
 * Her ürün için:
 *  - Ana satır: ürün adı, adet, birim/toplam fiyat
 *  - Alt satırlar (subItems):
 *      • variationName (varsa)
 *      • toppingsJson (DB'den) ya da rawJson.selectedToppings (eski sipariş)
 *      • rawJson.selectedChoices
 *      • v2026 ürün-level indirimler (sponsorship breakdown ile)
 *      • müşteri yorumu (Not: ...)
 *
 * @param order YS sipariş objesi (manuel modal'daki `order` veya hook'taki `d`)
 */
export const buildYemeksepetiReceiptItems = (order: YsOrderLike): PackageReceiptItem[] => {
  const items = order.items ?? order.Items ?? [];
  const rawParsed = parseYsRawJsonData(order.rawJsonData ?? order.RawJsonData);

  return items.map((it: YsOrderItemLike, idx: number): PackageReceiptItem => {
    const rawProd = rawParsed.products[idx];
    const subItems: { name: string }[] = [];

    const itName = it.name ?? it.Name ?? '';
    const variationName = it.variationName ?? it.VariationName;
    if (variationName && variationName !== itName) {
      subItems.push({ name: variationName });
    }

    const toppingsJson = it.toppingsJson ?? it.ToppingsJson;
    const dbToppings: any[] = (() => {
      try { return toppingsJson ? JSON.parse(toppingsJson) : []; } catch { return []; }
    })();
    const rawToppings = rawProd?.selectedToppings || [];
    const toppings = dbToppings.length > 0 ? dbToppings : rawToppings;
    subItems.push(...flattenOptions(toppings));

    const rawChoices = rawProd?.selectedChoices || [];
    if (rawChoices.length > 0) subItems.push(...flattenOptions(rawChoices));

    const itemDiscounts = getItemDiscounts(it, rawProd);
    if (itemDiscounts.length > 0) {
      subItems.push(...formatDiscountSubItems(itemDiscounts, 'Indirim', '  '));
    }

    const comment = it.comment ?? it.Comment ?? rawProd?.comment;
    if (comment) subItems.push({ name: `Not: ${comment}` });

    const originalUnitPrice = it.originalUnitPrice ?? it.OriginalUnitPrice;
    const unitPrice = it.unitPrice ?? it.UnitPrice ?? 0;
    const paidPrice = it.paidPrice ?? it.PaidPrice ?? 0;
    const quantity = it.quantity ?? it.Quantity ?? 1;

    return {
      name: itName || 'Ürün',
      quantity,
      unitPrice: originalUnitPrice ?? unitPrice,
      totalPrice: paidPrice,
      vatRate: 0,
      subItems: subItems.length > 0 ? subItems : undefined,
    };
  });
};
